import {
	useLocalParticipant,
	useMaybeRoomContext,
} from "@livekit/components-react";
import { captureException } from "@sentry/react";
import { Track } from "livekit-client";
import {
	ChevronUp,
	Mic,
	MicOff,
	Monitor,
	MonitorOff,
	PhoneOff,
	Smile,
	Video,
	VideoOff,
} from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useStore } from "zustand";
import { Button } from "@/components/ui/button";
import {
	Popover,
	PopoverAnchor,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import { useCameraPermission } from "@/hooks/use-camera-permission";
import { useMicrophonePermission } from "@/hooks/use-microphone-permission";
import { useScreenShare } from "@/hooks/use-screen-share";
import { useUser } from "@/hooks/use-user";
import { SENTRY_ENABLED } from "@/lib/sentry";
import { uiStore } from "@/lib/stores/ui-store";
import { ControlBarSettings } from "./control-bar-settings";
import { ScreenSourcePicker } from "./screen-source-picker";

type CustomControlBarProps = {
	compact?: boolean;
	container?: HTMLElement | null;
};

export function CustomControlBar({
	compact = false,
	container,
}: CustomControlBarProps) {
	const room = useMaybeRoomContext();
	const { localParticipant } = useLocalParticipant();
	const [audioSettingsOpen, setAudioSettingsOpen] = useState(false);
	const [videoSettingsOpen, setVideoSettingsOpen] = useState(false);
	const [reactionsOpen, setReactionsOpen] = useState(false);
	const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(
		null
	);
	const { getUserDisplayName } = useUser();
	const handleLeaveChannel = useStore(
		uiStore,
		(state) => state.handleLeaveChannel
	);
	const { requestPermission: requestMicPermission } = useMicrophonePermission();
	const { requestPermission: requestCameraPermission } = useCameraPermission();
	const {
		screenShareEnabled,
		toggleScreenShare,
		handleElectronScreenShare,
		screenPickerOpen,
		setScreenPickerOpen,
		screenSources,
	} = useScreenShare({
		localParticipant,
		sentryContext: "control_bar_screen_share",
	});

	useEffect(() => {
		if (container) {
			const ownerDocument = container.ownerDocument;
			setPortalContainer(ownerDocument.body);
		} else {
			setPortalContainer(null);
		}
	}, [container]);

	const micEnabled =
		localParticipant?.isMicrophoneEnabled &&
		localParticipant?.getTrackPublication(Track.Source.Microphone)?.track
			?.isMuted === false;

	const cameraEnabled =
		localParticipant?.isCameraEnabled &&
		localParticipant?.getTrackPublication(Track.Source.Camera)?.track
			?.isMuted === false;

	const toggleMic = async () => {
		if (!localParticipant) return;
		try {
			if (!micEnabled) {
				const granted = await requestMicPermission();
				if (!granted) return;
			}
			await localParticipant.setMicrophoneEnabled(!micEnabled);
		} catch (error) {
			if (SENTRY_ENABLED)
				captureException(error, {
					tags: { context: "control_bar_mic" },
				});
			toast.error("Failed to toggle microphone");
		}
	};

	const toggleCamera = async () => {
		if (!localParticipant) return;
		try {
			if (!cameraEnabled) {
				const granted = await requestCameraPermission();
				if (!granted) return;
			}
			await localParticipant.setCameraEnabled(!cameraEnabled);
		} catch (error) {
			if (SENTRY_ENABLED)
				captureException(error, {
					tags: { context: "control_bar_camera" },
				});
			toast.error("Failed to toggle camera");
		}
	};

	const leaveCall = () => {
		handleLeaveChannel();
		toast.info("Left voice channel");
	};

	const reactions = [
		{ emoji: "👍", label: "Thumbs up" },
		{ emoji: "👏", label: "Clap" },
		{ emoji: "✋", label: "Raised hand" },
		{ emoji: "❤️", label: "Heart" },
		{ emoji: "😂", label: "Laugh" },
		{ emoji: "🎉", label: "Celebrate" },
		{ emoji: "👎", label: "Thumbs down" },
		{ emoji: "🤔", label: "Thinking" },
	];

	const sendReaction = async (emoji: string) => {
		if (!(localParticipant && room)) return;
		try {
			const encoder = new TextEncoder();
			const participantId =
				localParticipant.identity || localParticipant.name || "anonymous";
			const displayName = getUserDisplayName(participantId);
			const data = encoder.encode(
				JSON.stringify({
					type: "reaction",
					emoji,
					participantName: displayName,
					timestamp: Date.now(),
				})
			);
			await localParticipant.publishData(data, { reliable: true });
			(room as any).emit("localReaction", {
				type: "reaction",
				emoji,
				participantName: displayName,
				timestamp: Date.now(),
			});
			setReactionsOpen(false);
		} catch (error) {
			if (SENTRY_ENABLED)
				captureException(error, {
					tags: { context: "control_bar_reaction" },
				});
			toast.error("Failed to send reaction");
		}
	};

	if (!room) return null;

	return (
		<>
			<ScreenSourcePicker
				onOpenChange={setScreenPickerOpen}
				onSelect={handleElectronScreenShare}
				open={screenPickerOpen}
				sources={screenSources}
			/>
			<div
				className="@container flex items-center justify-center gap-2 border-t bg-card/50 p-4 backdrop-blur-sm"
				data-tour="voice-controls"
			>
				<Popover onOpenChange={setAudioSettingsOpen} open={audioSettingsOpen}>
					<PopoverAnchor asChild>
						<div className="group flex items-center overflow-hidden rounded-full border border-border bg-background shadow-sm transition-all hover:shadow-md">
							<Button
								className={
									"@md:h-12 @sm:h-10 h-8 rounded-none border-0 bg-accent @md:px-4 @sm:px-3 px-2 transition-all hover:bg-accent-foreground/10"
								}
								onClick={toggleMic}
								variant="ghost"
							>
								{micEnabled ? (
									<Mic className="@md:size-5 @sm:size-4 size-3 text-foreground" />
								) : (
									<MicOff className="@md:size-5 @sm:size-4 size-3 text-foreground" />
								)}
								<span className="sr-only">
									{micEnabled ? "Mute microphone" : "Unmute microphone"}
								</span>
							</Button>
							{!compact && (
								<>
									<div className="h-6 w-px bg-border" />
									<PopoverTrigger asChild>
										<Button
											className={
												"@md:h-12 @sm:h-10 h-8 rounded-none border-0 bg-accent @md:px-2 @sm:px-1.5 px-1 transition-all hover:bg-accent-foreground/10"
											}
											variant="ghost"
										>
											<ChevronUp
												className={`@md:size-4 @sm:size-3 size-2.5 text-foreground transition-transform duration-200 ${
													audioSettingsOpen ? "rotate-180" : ""
												}`}
											/>
											<span className="sr-only">Audio settings</span>
										</Button>
									</PopoverTrigger>
								</>
							)}
						</div>
					</PopoverAnchor>
					<ControlBarSettings
						onOpenChange={setAudioSettingsOpen}
						open={audioSettingsOpen}
						type="audio"
					/>
				</Popover>

				<Popover onOpenChange={setVideoSettingsOpen} open={videoSettingsOpen}>
					<PopoverAnchor asChild>
						<div className="group flex items-center overflow-hidden rounded-full border border-border bg-background shadow-sm transition-all hover:shadow-md">
							<Button
								className={
									"@md:h-12 @sm:h-10 h-8 rounded-none border-0 bg-accent @md:px-4 @sm:px-3 px-2 transition-all hover:bg-accent-foreground/10"
								}
								onClick={toggleCamera}
								variant="ghost"
							>
								{cameraEnabled ? (
									<Video className="@md:size-5 @sm:size-4 size-3 text-foreground" />
								) : (
									<VideoOff className="@md:size-5 @sm:size-4 size-3 text-foreground" />
								)}
								<span className="sr-only">
									{cameraEnabled ? "Turn off camera" : "Turn on camera"}
								</span>
							</Button>
							{!compact && (
								<>
									<div className="h-6 w-px bg-border" />
									<PopoverTrigger asChild>
										<Button
											className={
												"@md:h-12 @sm:h-10 h-8 rounded-none border-0 bg-accent @md:px-2 @sm:px-1.5 px-1 transition-all hover:bg-accent-foreground/10"
											}
											variant="ghost"
										>
											<ChevronUp
												className={`@md:size-4 @sm:size-3 size-2.5 text-foreground transition-transform duration-200 ${
													videoSettingsOpen ? "rotate-180" : ""
												}`}
											/>
											<span className="sr-only">Video settings</span>
										</Button>
									</PopoverTrigger>
								</>
							)}
						</div>
					</PopoverAnchor>
					<ControlBarSettings
						onOpenChange={setVideoSettingsOpen}
						open={videoSettingsOpen}
						type="video"
					/>
				</Popover>

				<Button
					className="@md:h-12 @sm:h-10 h-8 rounded-full border border-border bg-accent @md:px-4 @sm:px-3 px-2 shadow-sm transition-all hover:shadow-md hover:brightness-90"
					onClick={toggleScreenShare}
					variant="ghost"
				>
					{screenShareEnabled ? (
						<MonitorOff className="@md:size-5 @sm:size-4 size-3 text-foreground" />
					) : (
						<Monitor className="@md:size-5 @sm:size-4 size-3 text-foreground" />
					)}
					<span className="sr-only">
						{screenShareEnabled ? "Stop sharing" : "Share screen"}
					</span>
				</Button>

				<Popover onOpenChange={setReactionsOpen} open={reactionsOpen}>
					<PopoverTrigger asChild>
						<Button
							className="@md:h-12 @sm:h-10 h-8 rounded-full border border-border bg-accent @md:px-4 @sm:px-3 px-2 shadow-sm transition-all hover:shadow-md hover:brightness-90"
							variant="ghost"
						>
							<Smile className="@md:size-5 @sm:size-4 size-3 text-foreground" />
							<span className="sr-only">Send reaction</span>
						</Button>
					</PopoverTrigger>
					<PopoverContent
						align="center"
						className="w-auto p-2"
						container={portalContainer}
						side="top"
					>
						<div className="grid grid-cols-4 gap-1">
							{reactions.map((reaction) => (
								<Button
									className="h-12 w-12 text-2xl transition-transform hover:scale-110"
									key={reaction.emoji}
									onClick={() => sendReaction(reaction.emoji)}
									title={reaction.label}
									variant="ghost"
								>
									{reaction.emoji}
								</Button>
							))}
						</div>
					</PopoverContent>
				</Popover>

				<Button
					className="@md:h-12 @sm:h-10 h-8 rounded-full @md:px-6 @sm:px-4 px-3 shadow-sm transition-all hover:shadow-md"
					onClick={leaveCall}
					variant="destructive"
				>
					<PhoneOff className="@md:size-5 @sm:size-4 size-3" />
					<span className="sr-only">Leave call</span>
				</Button>
			</div>
		</>
	);
}
